import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { useApp } from '@/context/AppContext';
import { Award, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import veriLinkLogo from '@/assets/verilink-logo.png';

export const MainLayout = ({ children, onShowAuth }) => {
  const { newBadge } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Mobile Header - Only visible on mobile/tablet */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img 
              src={veriLinkLogo} 
              alt="VeriLink Logo" 
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="font-display text-lg font-bold text-foreground">VeriLink</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="h-10 w-10"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <MobileNav 
          onClose={() => setMobileMenuOpen(false)} 
          onShowAuth={onShowAuth}
        />
      )}

      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar onShowAuth={onShowAuth} />
      </div>
      
      {/* Main Content */}
      <main className="min-h-screen pt-16 lg:pt-0 lg:ml-72">
        {/* Mobile: Full width with padding, Desktop: Max container */}
        <div className="px-4 py-6 sm:px-6 lg:p-8">
          <div className="max-w-6xl mx-auto animate-fade-in">
            {children}
          </div>
        </div>
      </main>

      {/* New Badge Notification - Responsive positioning */}
      {newBadge && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 animate-slide-in">
          <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-success/30 sm:max-w-sm">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl gradient-success flex items-center justify-center animate-bounce-soft flex-shrink-0">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-success-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-success font-medium uppercase tracking-wide">
                  New Badge Unlocked!
                </p>
                <p className="font-display font-semibold text-foreground mt-1 truncate">
                  {newBadge.name}
                </p>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {newBadge.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background decorative elements - Smaller/Hidden on mobile */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="hidden sm:block absolute top-20 right-20 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="hidden sm:block absolute bottom-20 left-1/3 w-80 h-80 rounded-full bg-accent/5 blur-3xl" />
      </div>
    </div>
  );
};

export default MainLayout;
